import axios from 'axios'
import Router from '~/router'
import AuthService from 'services/AuthService'
import ValidationService from 'services/ValidationService'

function createResponse({ data, headers, status }) {
    let contentType = headers['content-type'] || ''
    const response =
        (contentType.indexOf('application/json') !== -1 || contentType.indexOf('application/pdf') !== -1) &&
        typeof data === 'object'
            ? data
            : {}

    response._headers = headers
    response._status = status

    return response
}

function toAppResponse(axiosResponse) {
    if (!axiosResponse) {
        return { _headers: {}, _status: 0 }
    }
    return createResponse(axiosResponse)
}

function isExpiredAuthResponse(response) {
    if (response._status === 401) {
        return true
    }
    const message = String(response.message || '')
    return response._status === 403 && /token has expired/i.test(message)
}

function setAuthorizationHeader(config, token) {
    config.headers = config.headers || {}
    if (typeof config.headers.set === 'function') {
        config.headers.set('Authorization', `Bearer ${token}`)
        return
    }
    config.headers.Authorization = `Bearer ${token}`
}

export function redirectToLogin() {
    const currentRoute = Router.currentRoute
    if (currentRoute && currentRoute.name === 'auth.login') {
        return
    }
    Router.replace({ name: 'auth.login' })
}

function createErrorObject(error) {
    function ErrorObject() {
        this.code = error.code
        this.field = error.field
        this.message = error.message
    }

    ErrorObject.prototype.toString = function () {
        return this.message
    }

    return new ErrorObject()
}

function extractErrors({ response: { data: errors } }) {
    return errors.reduce((messages, error) => {
        messages[error.field] = createErrorObject(error)
        return messages
    }, {})
}

axios.defaults.baseURL = env('VUE_APP_API_URL')
axios.defaults.timeout = 20000

axios.interceptors.request.use(async (config) => {
    if (!AuthService.isLoggedIn()) {
        return config
    }

    try {
        const token = await AuthService.getValidToken()
        if (!token) {
            AuthService.logOut()
            redirectToLogin()
            return Promise.reject(new axios.Cancel('Session expired'))
        }
        setAuthorizationHeader(config, token)
        return config
    } catch {
        // refreshTokens() already cleared the session
        redirectToLogin()
        return Promise.reject(new axios.Cancel('Session expired'))
    }
})

axios.interceptors.response.use(
    (response) => createResponse(response),
    async (error) => {
        if (axios.isCancel(error)) {
            return Promise.reject(error)
        }

        const originalRequest = error.config || {}
        error.response = toAppResponse(error.response)

        if (error.response._status === 400 && error.response.code === 'validation_failed') {
            ValidationService.set(extractErrors(error))
        }

        if (!isExpiredAuthResponse(error.response) || !AuthService.isLoggedIn()) {
            return Promise.reject(error)
        }

        if (originalRequest._retry) {
            AuthService.logOut()
            redirectToLogin()
            return Promise.reject(error)
        }

        originalRequest._retry = true

        try {
            const token = await AuthService.refreshTokens()
            setAuthorizationHeader(originalRequest, token)
            return axios(originalRequest)
        } catch {
            redirectToLogin()
            return Promise.reject(error)
        }
    },
)

export default axios
